import { AlphaFilter, Container, Sprite, Texture } from 'pixi.js';
import { PixiOverlayHost } from './PixiOverlayHost';
import type { IRibbonView, RibbonRun, RibbonViewStyle } from './utils/RibbonView';

/** Diameter in px of the baked cap disc (scaled to each joint's width). */
const CAP_PX = 32;
let capTexture: Texture | null = null;

/** One white disc on its own canvas source, shared by every ribbon's caps. */
function getCapTexture(): Texture {
  if (capTexture) return capTexture;
  try {
    const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
    const ctx = canvas?.getContext('2d') ?? null;
    if (canvas && ctx && typeof Texture.from === 'function') {
      canvas.width = CAP_PX;
      canvas.height = CAP_PX;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(CAP_PX / 2, CAP_PX / 2, CAP_PX / 2 - 0.5, 0, Math.PI * 2);
      ctx.fill();
      capTexture = Texture.from(canvas);
      return capTexture;
    }
  } catch { /* no 2D canvas — square caps */ }
  return Texture.WHITE;
}

/**
 * PixiRibbonView — a trail ribbon as two runs of white segment sprites
 * (plan 16 §2.4). Each segment is `Texture.WHITE` anchored at its start,
 * rotated to the next point, scaled to (length, width) and tinted, so a
 * moving ribbon only writes transforms, tints and alphas. The old ribbon was
 * one Graphics cleared and re-stroked every frame — a stage rebuild per
 * frame for every live trail.
 *
 * Joints are rounded (DJ, 2026-10-08): each interior joint carries one small
 * disc sprite at the joint's width, tint and alpha, so bends read as one
 * smooth stroke instead of square segment ends. Caps are pooled per run like
 * the segments and posed by transform/tint/alpha only.
 *
 * The glow run is one uniform alpha, so it is drawn opaque and faded as a
 * whole by an AlphaFilter on its container: overlapping segments and caps
 * then read as one stroke (the old single polyline), with no brighter beads
 * where they overlap. The filter is toggled by `enabled`, a runtime flag
 * that Pixi checks per render — never a structural change.
 *
 * Runs only grow (a sprite added once per new slot); unused slots and a
 * parked view sit at alpha 0. `visible`, `zIndex` and the parent never change
 * after the view is attached.
 */
interface RunSlots {
  container: Container | null;
  sprites: Sprite[];
  cursor: number;
  caps: Sprite[];
  capCursor: number;
}

export class PixiRibbonView implements IRibbonView {
  private root: Container | null = null;
  private runs: Record<RibbonRun, RunSlots> = {
    glow: { container: null, sprites: [], cursor: 0, caps: [], capCursor: 0 },
    core: { container: null, sprites: [], cursor: 0, caps: [], capCursor: 0 },
  };
  private destroyed = false;
  private parked = false;
  /** Fades the opaque glow run as one layer (null without filter support). */
  private glowFilter: AlphaFilter | null = null;

  constructor(style: RibbonViewStyle) {
    const ownerStack = (globalThis as { __renderAttribLevel?: number }).__renderAttribLevel! >= 3
      ? new Error().stack : undefined;
    void PixiOverlayHost.ensureReady().then(() => {
      if (this.destroyed) return;
      const root = new Container();
      root.label = 'RibbonView';
      if (ownerStack) (root as Container & { __lvOwnerStack?: string }).__lvOwnerStack = ownerStack;
      root.zIndex = style.depth;
      if (style.glow) root.blendMode = 'add';
      root.alpha = this.parked ? 0 : 1;
      const glow = new Container();
      const core = new Container();
      glow.label = 'RibbonView';
      core.label = 'RibbonView';
      if (typeof AlphaFilter === 'function') {
        this.glowFilter = new AlphaFilter({ alpha: 0 });
        this.glowFilter.enabled = false;
        glow.filters = [this.glowFilter];
      }
      root.addChild(glow);
      root.addChild(core);
      this.runs.glow.container = glow;
      this.runs.core.container = core;
      this.root = root;
      if (style.glow) PixiOverlayHost.addWorldGlowChild(root);
      else PixiOverlayHost.addWorldChild(root);
    });
  }

  begin(): void {
    for (const r of [this.runs.glow, this.runs.core]) {
      r.cursor = 0;
      r.capCursor = 0;
    }
  }

  joint(run: RibbonRun, x: number, y: number, width: number, color: number, alpha: number): void {
    const r = this.runs[run];
    if (!r.container) return;
    let s = r.caps[r.capCursor];
    if (!s) {
      s = new Sprite(getCapTexture());
      s.anchor.set(0.5, 0.5);
      s.alpha = 0;
      r.container.addChild(s);
      r.caps.push(s);
    }
    r.capCursor++;
    const k = width / (s.texture.width || 1);
    s.position.set(x, y);
    s.scale.set(k, k);
    s.tint = color;
    s.alpha = this.runAlpha(run, alpha);
  }

  segment(run: RibbonRun, x1: number, y1: number, x2: number, y2: number, width: number, color: number, alpha: number): void {
    const r = this.runs[run];
    if (!r.container) return;
    let s = r.sprites[r.cursor];
    if (!s) {
      s = new Sprite(Texture.WHITE);
      s.anchor.set(0, 0.5);
      s.alpha = 0;
      r.container.addChild(s);
      r.sprites.push(s);
    }
    r.cursor++;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const tw = s.texture.width || 1;
    const th = s.texture.height || 1;
    s.position.set(x1, y1);
    s.rotation = Math.atan2(dy, dx);
    s.scale.set(Math.sqrt(dx * dx + dy * dy) / tw, width / th);
    s.tint = color;
    s.alpha = this.runAlpha(run, alpha);
  }

  end(): void {
    for (const run of [this.runs.glow, this.runs.core]) {
      for (let i = run.cursor; i < run.sprites.length; i++) run.sprites[i].alpha = 0;
      for (let i = run.capCursor; i < run.caps.length; i++) run.caps[i].alpha = 0;
    }
    if (this.glowFilter) this.glowFilter.enabled = !this.parked && this.runs.glow.cursor > 0;
  }

  /** Glow sprites draw opaque under the run's AlphaFilter; the rest carry their alpha. */
  private runAlpha(run: RibbonRun, alpha: number): number {
    if (run !== 'glow' || !this.glowFilter) return alpha;
    this.glowFilter.alpha = alpha;
    return 1;
  }

  park(): void {
    this.parked = true;
    if (this.root) this.root.alpha = 0;
    if (this.glowFilter) this.glowFilter.enabled = false;
  }

  unpark(): void {
    this.parked = false;
    if (this.root) this.root.alpha = 1;
  }

  destroy(): void {
    this.destroyed = true;
    this.root?.destroy({ children: true });
    this.root = null;
  }
}
